// skipcq: JS-W1028
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import Autocomplete from '@mui/material/Autocomplete';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import NoteAltOutlinedIcon from '@mui/icons-material/NoteAltOutlined';
import VerifiedIcon from '@mui/icons-material/Verified';

import BrewhousePageShell from '../../components/brewhouse/BrewhousePageShell';
import { useBreweryPortalSearch } from '../../hooks/useBreweryPortalSearch';
import { breweryPortalAutocompleteLabel } from '../../utils/breweryPortalOptionLabel';
import {
  BREWERY_CLAIM_PROOF_ACCEPT,
  BREWERY_CLAIM_PROOF_HELPER_TEXT,
  createBreweryClaimWithProofUpload,
  findBlockingClaimStatus,
} from '../../services/breweryClaims';
import { auth, db } from '../../utils/firebase';
import {
  finishClaimBreweryHandoff,
  takeClaimBreweryHandoff,
} from '../../utils/claimBreweryHandoff';
import { trackClaimPrefillBrewery } from '../../utils/brewhousePortalAnalytics';

function passThroughAutocompleteFilter(options) {
  return options;
}

function isBreweryOptionEqual(optionA, optionB) {
  return optionA?.id === optionB?.id;
}

function ProofTypeColumn({ iconSlot, captionLabel }) {
  return (
    <Stack
      alignItems="center"
      spacing={0.75}
      sx={{ flex: '1 1 7rem', minWidth: 0, textAlign: 'center' }}
    >
      {iconSlot}
      <Typography variant="caption" component="p" color="text.secondary">
        {captionLabel}
      </Typography>
    </Stack>
  );
}

function VerifiedIconWrap({ children }) {
  return (
    <Badge
      overlap="circular"
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      badgeContent={
        <VerifiedIcon color="primary" sx={{ fontSize: 18 }} titleAccess="Approved document type" />
      }
    >
      {children}
    </Badge>
  );
}

const CLAIM_PREFILL_MISS_MESSAGE = 'We could not load that brewery. Search for it by name.';

function resolveSearchHelperText(duplicateHint, searchLoading, searchError, prefillNotice) {
  if (duplicateHint === 'pending') {
    return 'You already have a pending claim for this brewery. See claim status.';
  }
  if (duplicateHint === 'approved') {
    return 'You already manage this brewery.';
  }
  if (searchLoading) {
    return 'Searching…';
  }
  if (searchError) {
    return searchError;
  }
  if (typeof prefillNotice === 'string' && prefillNotice.length > 0) {
    return prefillNotice;
  }
  return ' ';
}

function BreweryClaimSearchAutocomplete({
  breweries,
  breweryOption,
  onBreweryChange,
  autocompleteInput,
  onAutocompleteInputChange,
  searchLoading,
  duplicateHint,
  searchError,
  showNoResults,
  prefillNotice,
}) {
  const renderSearchField = useCallback(
    (params) => (
      <TextField
        {...params}
        label="Search breweries"
        placeholder="Type at least two characters"
        margin="normal"
        helperText={resolveSearchHelperText(
          duplicateHint,
          searchLoading,
          searchError,
          prefillNotice,
        )}
        error={Boolean(searchError) || duplicateHint === 'pending' || duplicateHint === 'approved'}
      />
    ),
    [duplicateHint, prefillNotice, searchError, searchLoading],
  );

  return (
    <>
      <Autocomplete
        freeSolo={false}
        options={breweries}
        filterOptions={passThroughAutocompleteFilter}
        getOptionLabel={breweryPortalAutocompleteLabel}
        isOptionEqualToValue={isBreweryOptionEqual}
        value={breweryOption}
        onChange={onBreweryChange}
        inputValue={autocompleteInput}
        onInputChange={onAutocompleteInputChange}
        loading={searchLoading}
        renderInput={renderSearchField}
      />
      {showNoResults ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          No brewery found.
        </Typography>
      ) : null}
    </>
  );
}

function BreweryClaimStepOne({
  breweries,
  breweryOption,
  onBreweryChange,
  autocompleteInput,
  onAutocompleteInputChange,
  searchLoading,
  duplicateHint,
  searchError,
  showNoResults,
  prefillNotice,
}) {
  return (
    <Box sx={{ flex: 1, pr: { md: 3 } }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
        Step 1: Find your brewery
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
        Start typing the name of your brewery and select it from the drop down.
      </Typography>
      <BreweryClaimSearchAutocomplete
        breweries={breweries}
        breweryOption={breweryOption}
        onBreweryChange={onBreweryChange}
        autocompleteInput={autocompleteInput}
        onAutocompleteInputChange={onAutocompleteInputChange}
        searchLoading={searchLoading}
        duplicateHint={duplicateHint}
        searchError={searchError}
        showNoResults={showNoResults}
        prefillNotice={prefillNotice}
      />
    </Box>
  );
}

function BreweryClaimStepTwo({
  proofFile,
  submitError,
  submitDisabled,
  submitting,
  onOpenUpload,
  onFileChange,
  onCancel,
  onSubmit,
  fileInputRef,
}) {
  return (
    <Box sx={{ flex: 1, pl: { md: 3 }, textAlign: 'left' }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
        Step 2: Verify your brewery
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
        Upload 1 Utility bill as official proof of your brewery.
      </Typography>

      <Stack direction="row" justifyContent="center" sx={{ mb: 3 }}>
        <ProofTypeColumn
          iconSlot={(
            <VerifiedIconWrap>
              <NoteAltOutlinedIcon sx={{ fontSize: 44 }} color="action" />
            </VerifiedIconWrap>
          )}
          captionLabel="Utility bill"
        />
      </Stack>

      <input
        ref={fileInputRef}
        type="file"
        accept={BREWERY_CLAIM_PROOF_ACCEPT}
        style={{ display: 'none' }}
        onChange={onFileChange}
      />
      <Stack spacing={0.75}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
          <Button type="button" variant="outlined" onClick={onOpenUpload}>
            Upload
          </Button>
          <TextField
            size="small"
            label="Selected file"
            value={proofFile ? proofFile.name : ''}
            placeholder="No file selected"
            InputProps={{ readOnly: true }}
            sx={{ minWidth: 200, flex: '1 1 200px' }}
          />
        </Stack>
        <Typography variant="caption" color="text.secondary" component="p" sx={{ m: 0 }}>
          {BREWERY_CLAIM_PROOF_HELPER_TEXT}
        </Typography>
      </Stack>

      {submitError ? (
        <Typography variant="body2" color="error" sx={{ mt: 2 }}>
          {submitError}
        </Typography>
      ) : null}

      <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
        <Button type="button" variant="outlined" color="inherit" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          type="button"
          variant="contained"
          color="secondary"
          disabled={submitDisabled}
          onClick={onSubmit}
        >
          {submitting ? <CircularProgress size={22} color="inherit" /> : 'Submit'}
        </Button>
      </Stack>

    </Box>
  );
}

function BreweryClaimForm() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [autocompleteInput, setAutocompleteInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const timerId = window.setTimeout(() => {
      setDebouncedSearch(autocompleteInput);
    }, 300);
    return () => window.clearTimeout(timerId);
  }, [autocompleteInput]);

  const { breweries, isLoading: searchLoading, error: searchError } = useBreweryPortalSearch(
    debouncedSearch,
  );

  const [breweryOption, setBreweryOption] = useState(null);
  const [duplicateHint, setDuplicateHint] = useState(null);
  const [proofFile, setProofFile] = useState(null);
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [prefillNotice, setPrefillNotice] = useState('');

  const breweryOptionRef = useRef(null);
  const userEditedDuringPrefillRef = useRef(false);
  useEffect(() => {
    breweryOptionRef.current = breweryOption;
  }, [breweryOption]);

  const runDuplicateCheck = useCallback(async (selected) => {
    const user = auth.currentUser;
    if (!user || !selected?.id) {
      setDuplicateHint(null);
      return;
    }
    const expectedBreweryId = selected.id;
    const blockState = await findBlockingClaimStatus(user.uid, expectedBreweryId);
    if (breweryOptionRef.current?.id !== expectedBreweryId) {
      return;
    }
    if (blockState === 'pending') {
      setDuplicateHint('pending');
      return;
    }
    if (blockState === 'approved') {
      setDuplicateHint('approved');
      return;
    }
    setDuplicateHint(null);
  }, []);

  useEffect(() => {
    const handoff = takeClaimBreweryHandoff();
    const breweryId = handoff.breweryId;
    if (!breweryId) {
      return undefined;
    }
    let cancelled = false;

    async function loadPrefillBrewery() {
      try {
        const snapshot = await getDoc(doc(db, 'breweries', breweryId));
        if (cancelled || userEditedDuringPrefillRef.current) {
          return;
        }
        if (!snapshot.exists()) {
          setPrefillNotice(CLAIM_PREFILL_MISS_MESSAGE);
          trackClaimPrefillBrewery({ breweryId, outcome: 'miss' });
          return;
        }
        const breweryData = snapshot.data() || {};
        const option = {
          id: snapshot.id,
          data: {
            name: typeof breweryData.name === 'string' ? breweryData.name : '',
            city: typeof breweryData.city === 'string' ? breweryData.city : '',
            state: typeof breweryData.state === 'string' ? breweryData.state : '',
          },
        };
        breweryOptionRef.current = option;
        setBreweryOption(option);
        setAutocompleteInput(breweryPortalAutocompleteLabel(option));
        setPrefillNotice('');
        trackClaimPrefillBrewery({ breweryId, outcome: 'success' });
        runDuplicateCheck(option).catch(() => {
          /* ignore duplicate-check errors; UI stays usable */
        });
      } catch {
        if (!cancelled && !userEditedDuringPrefillRef.current) {
          setPrefillNotice(CLAIM_PREFILL_MISS_MESSAGE);
          trackClaimPrefillBrewery({ breweryId, outcome: 'miss' });
        }
      }
    }

    loadPrefillBrewery().catch(() => {
      // Errors are recorded inside loadPrefillBrewery.
    });

    return () => {
      cancelled = true;
      finishClaimBreweryHandoff(handoff.ticket);
    };
  }, [runDuplicateCheck]);

  const handleBreweryChange = useCallback(
    (_, newValue) => {
      userEditedDuringPrefillRef.current = true;
      setBreweryOption(newValue);
      setSubmitError('');
      setPrefillNotice('');
      if (newValue) {
        setAutocompleteInput(breweryPortalAutocompleteLabel(newValue));
        runDuplicateCheck(newValue).catch(() => {
          /* ignore duplicate-check errors; UI stays usable */
        });
      } else {
        setAutocompleteInput('');
        setDuplicateHint(null);
      }
    },
    [runDuplicateCheck],
  );

  const handleAutocompleteInputChange = useCallback((_event, nextInput, reason) => {
    if (reason === 'reset') {
      return;
    }
    if (reason === 'input' || reason === 'clear') {
      userEditedDuringPrefillRef.current = true;
      setPrefillNotice('');
    }
    setAutocompleteInput(nextInput);
    const currentOption = breweryOptionRef.current;
    if (!currentOption) {
      return;
    }
    const expectedLabel = breweryPortalAutocompleteLabel(currentOption);
    if (nextInput !== expectedLabel) {
      setDuplicateHint(null);
    }
  }, []);

  const handleOpenUpload = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback((event) => {
    const picked = event.target.files?.[0];
    setSubmitError('');
    if (!picked) {
      setProofFile(null);
      return;
    }
    setProofFile(picked);
  }, []);

  const handleCancel = useCallback(() => {
    userEditedDuringPrefillRef.current = true;
    setAutocompleteInput('');
    setDebouncedSearch('');
    setBreweryOption(null);
    setDuplicateHint(null);
    setProofFile(null);
    setSubmitError('');
    setPrefillNotice('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const handleSubmit = useCallback(async () => {
    setSubmitError('');
    if (!breweryOption?.id) {
      setSubmitError('Select a brewery from the list.');
      return;
    }
    if (!proofFile) {
      setSubmitError('Upload proof of ownership.');
      return;
    }
    const dataRecord = breweryOption.data || {};
    const breweryName =
      typeof dataRecord.name === 'string' ? dataRecord.name : String(breweryOption.id);
    const breweryCity = typeof dataRecord.city === 'string' ? dataRecord.city : '';
    const breweryState = typeof dataRecord.state === 'string' ? dataRecord.state : '';

    setSubmitting(true);
    try {
      await createBreweryClaimWithProofUpload({
        breweryId: breweryOption.id,
        breweryName,
        breweryCity,
        breweryState,
        proofFile,
      });
      navigate('/brewery-portal/status', { replace: true });
    } catch (error) {
      if (error?.code === 'DUPLICATE_CLAIM') {
        const dest =
          error.blockingStatus === 'approved'
            ? '/brewery-portal/dashboard'
            : '/brewery-portal/status';
        navigate(dest, { replace: true });
        return;
      }
      const message = typeof error?.message === 'string' ? error.message : 'Submit failed. Please try again.';
      setSubmitError(message);
    } finally {
      setSubmitting(false);
    }
  }, [breweryOption, proofFile, navigate]);

  const handleSubmitClick = useCallback(() => {
    handleSubmit();
  }, [handleSubmit]);

  const trimmedInput = autocompleteInput.trim();
  const minCharsForSearch = 2;
  const showNoResults =
    !searchLoading &&
    !searchError &&
    trimmedInput.length >= minCharsForSearch &&
    breweries.length === 0;

  const submitDisabled =
    submitting ||
    !breweryOption ||
    !proofFile ||
    duplicateHint === 'pending' ||
    duplicateHint === 'approved';

  return (
    <BrewhousePageShell
      breadcrumbs={[{ label: 'Claim' }]}
      title="Claim your brewery"
      maxWidth={960}
    >
      <Card
        variant="outlined"
        sx={{
          borderRadius: 2,
          boxShadow: (theme) => theme.shadows[1],
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            divider={<Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />}
            sx={{ alignItems: 'stretch', gap: { xs: 2, md: 0 } }}
          >
            <BreweryClaimStepOne
              breweries={breweries}
              breweryOption={breweryOption}
              onBreweryChange={handleBreweryChange}
              autocompleteInput={autocompleteInput}
              onAutocompleteInputChange={handleAutocompleteInputChange}
              searchLoading={searchLoading}
              duplicateHint={duplicateHint}
              searchError={searchError}
              showNoResults={showNoResults}
              prefillNotice={prefillNotice}
            />
            <Divider sx={{ display: { xs: 'block', md: 'none' } }} />
            <BreweryClaimStepTwo
              proofFile={proofFile}
              submitError={submitError}
              submitDisabled={submitDisabled}
              submitting={submitting}
              onOpenUpload={handleOpenUpload}
              onFileChange={handleFileChange}
              onCancel={handleCancel}
              onSubmit={handleSubmitClick}
              fileInputRef={fileInputRef}
            />
          </Stack>
        </CardContent>
      </Card>
    </BrewhousePageShell>
  );
}

export default function BreweryClaimPage() {
  return <BreweryClaimForm />;
}
